import { MARKV_TRACE_SIZE, MarkVTraceLoader } from "@/components/compass/MarkVTraceLoader"
import { cn } from "@/lib/cn"

/*
 * MARK V — espera que cobre uma área. Scrim + loader Traço centralizado.
 *
 * O pai precisa ser `relative`: o overlay é `absolute inset-0`, então cobre o
 * bloco onde vive (card, painel, tabela) e não a viewport inteira.
 *
 * O scrim usa color-mix sobre --markv-bg em vez do rgba(255,255,255,.94) do
 * arquivo do design: branco fixo apareceria como um retângulo claro por cima da
 * superfície escura no dark. Com o token, o véu acompanha o tema.
 *
 * Fechado não renderiza nada. Esconder por CSS deixaria o texto do loader vivo
 * na árvore de acessibilidade, anunciando espera que já terminou.
 */

/*
 * Peso do véu, e a escolha não é estética:
 *
 *   forte — cobre CONTEÚDO de verdade (dialog, tabela, dropzone). Precisa
 *           esconder o que está atrás, senão a tela lê como interativa.
 *   leve  — cobre ESQUELETO. Ali não há nada a esconder, e um véu forte apaga
 *           a forma reservada: o esqueleto vira enfeite e sobra o V no vazio.
 */
const SCRIM: Record<"forte" | "leve", string> = {
  forte: "bg-[color-mix(in_srgb,var(--markv-bg)_94%,transparent)]",
  leve: "bg-[color-mix(in_srgb,var(--markv-bg)_45%,transparent)]",
}

export type LoadingOverlayScrim = keyof typeof SCRIM

/*
 * "area" cobre o bloco onde o overlay vive (pai precisa ser relative). "tela"
 * cobre a viewport, para espera que troca o app inteiro: numa troca de empresa
 * um absolute ficaria preso ao dropdown do header e cobriria um retângulo de
 * 200px enquanto o resto da tela segue com os dados da empresa anterior.
 */
const COBERTURA = {
  area: "absolute z-10",
  tela: "fixed z-50",
} as const

export type LoadingOverlayCobertura = keyof typeof COBERTURA

export type LoadingOverlayProps = {
  open: boolean
  /** Texto anunciado ao leitor de tela. */
  label?: string
  /** Peso do véu. Default "forte". Use "leve" sobre esqueleto. */
  scrim?: LoadingOverlayScrim
  /** Alcance. Default "area". Use "tela" quando a espera troca o app inteiro. */
  cobertura?: LoadingOverlayCobertura
  className?: string
}

export function LoadingOverlay({
  open,
  label = "Carregando",
  scrim = "forte",
  cobertura = "area",
  className,
}: LoadingOverlayProps) {
  if (!open) return null

  return (
    <div
      data-testid="loading-overlay"
      aria-busy="true"
      className={cn(
        COBERTURA[cobertura],
        "inset-0 flex items-center justify-center",
        SCRIM[scrim],
        "animate-[mvTraceOverlayIn_160ms_linear_both]",
        className,
      )}
    >
      <span role="status" aria-live="polite" className="sr-only">
        {label}
      </span>
      <MarkVTraceLoader size={MARKV_TRACE_SIZE.overlay} label="" />
    </div>
  )
}
